'use client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { Star, X, Navigation } from 'lucide-react';
import type { Cafe } from '@/data/mockCafes';
import {
  GoogleMap,
  useJsApiLoader,
  MarkerClusterer,
  Marker,
  InfoWindow,
} from '@react-google-maps/api';

/// <reference types="@types/google.maps" />

interface Props {
  cafes: Cafe[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onBoundsChange?: (visibleIds: string[]) => void;
}

const MALANG_CENTER = { lat: -7.9666, lng: 112.6326 };

const MAP_STYLES: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#f5ebe0' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5c4033' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#fff8f0' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#e8d5be' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#d4b896' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#c9a87c' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#a8d5e2' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#c8e6c9' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
];

const MAP_OPTIONS: google.maps.MapOptions = {
  styles: MAP_STYLES,
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

const CLUSTERER_OPTIONS = {
  maxZoom: 14,
  gridSize: 60,
  styles: [
    {
      textColor: '#fff',
      url: '',
      height: 44,
      width: 44,
      textSize: 13,
    },
  ],
};

function createPinSvg(isSelected: boolean, isOpen: boolean): string {
  const bg = isSelected ? '#D97706' : isOpen ? '#92400E' : '#9CA3AF';
  const border = isSelected ? '#FFF' : '#FFF';
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <ellipse cx="18" cy="40" rx="6" ry="3" fill="rgba(0,0,0,0.2)"/>
      <path d="M18 2C10.268 2 4 8.268 4 16c0 10 14 26 14 26S32 26 32 16C32 8.268 25.732 2 18 2z" fill="${bg}" stroke="${border}" stroke-width="2"/>
      <text x="18" y="20" text-anchor="middle" font-size="14" fill="white">☕</text>
    </svg>
  `)}`;
}

export default function MapPanel({ cafes, selectedId, onSelect, onBoundsChange }: Props) {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '',
    id: 'google-map-script',
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const [infoWindowId, setInfoWindowId] = useState<string | null>(null);
  const selectedCafe = cafes.find((c) => c.id === selectedId) || (infoWindowId ? cafes.find((c) => c.id === infoWindowId) : null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
  }, []);

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleBoundsChanged = useCallback(() => {
    if (!mapRef.current || !onBoundsChange) return;
    const bounds = mapRef.current.getBounds();
    if (!bounds) return;
    const visibleIds = cafes
      .filter((c) => bounds.contains({ lat: c.lat, lng: c.lng }))
      .map((c) => c.id);
    onBoundsChange(visibleIds);
  }, [cafes, onBoundsChange]);

  // Pan to selected cafe when selectedId changes
  useEffect(() => {
    if (selectedId && mapRef.current) {
      const cafe = cafes.find((c) => c.id === selectedId);
      if (cafe) {
        mapRef.current.panTo({ lat: cafe.lat, lng: cafe.lng });
        mapRef.current.setZoom(16);
        setInfoWindowId(selectedId);
      }
    }
  }, [selectedId, cafes]);

  const handleNearMe = useCallback(() => {
    if (!navigator.geolocation || !mapRef.current) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        mapRef.current?.panTo(loc);
        mapRef.current?.setZoom(14);
      },
      () => {
        mapRef.current?.panTo(MALANG_CENTER);
        mapRef.current?.setZoom(13);
      }
    );
  }, []);

  // Fallback when API key is missing or load error
  if (loadError || !process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY === 'your-google-maps-api-key-here') {
    return <FallbackMap cafes={cafes} selectedId={selectedId} onSelect={onSelect} />;
  }

  if (!isLoaded) {
    return (
      <div className="w-full h-full rounded-2xl overflow-hidden border border-border bg-secondary flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-muted-foreground font-500">Memuat peta...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full rounded-2xl overflow-hidden border border-border">
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={MALANG_CENTER}
        zoom={13}
        options={MAP_OPTIONS}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        onBoundsChanged={handleBoundsChanged}
        onClick={() => {
          setInfoWindowId(null);
          onSelect(null);
        }}
      >
        <MarkerClusterer
          options={CLUSTERER_OPTIONS}
          calculator={(markers) => ({
            text: String(markers.length),
            index: 1,
            title: `${markers.length} coffeeshop`,
          })}
          styles={[
            {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
                  <circle cx="24" cy="24" r="22" fill="#D97706" stroke="white" stroke-width="3"/>
                  <text x="24" y="29" text-anchor="middle" font-size="14" font-weight="bold" fill="white" font-family="sans-serif">☕</text>
                </svg>
              `)}`,
              height: 48,
              width: 48,
              textColor: 'transparent',
              textSize: 0,
            },
          ]}
        >
          {(clusterer) => (
            <>
              {cafes.map((cafe) => {
                const isSelected = cafe.id === selectedId || cafe.id === infoWindowId;
                return (
                  <Marker
                    key={`marker-${cafe.id}`}
                    position={{ lat: cafe.lat, lng: cafe.lng }}
                    clusterer={clusterer}
                    icon={{
                      url: createPinSvg(isSelected, cafe.isOpen),
                      scaledSize: new window.google.maps.Size(isSelected ? 44 : 36, isSelected ? 54 : 44),
                      anchor: new window.google.maps.Point(isSelected ? 22 : 18, isSelected ? 54 : 44),
                    }}
                    zIndex={isSelected ? 999 : 1}
                    onClick={() => {
                      onSelect(cafe.id);
                      setInfoWindowId(cafe.id);
                    }}
                    title={cafe.name}
                  />
                );
              })}
            </>
          )}
        </MarkerClusterer>

        {/* InfoWindow for selected cafe */}
        {infoWindowId && (() => {
          const cafe = cafes.find((c) => c.id === infoWindowId);
          if (!cafe) return null;
          return (
            <InfoWindow
              position={{ lat: cafe.lat, lng: cafe.lng }}
              onCloseClick={() => {
                setInfoWindowId(null);
                onSelect(null);
              }}
              options={{ pixelOffset: new window.google.maps.Size(0, -48) }}
            >
              <div style={{ minWidth: 200, maxWidth: 240, fontFamily: 'sans-serif' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <img
                    src={cafe.photos[0]}
                    alt={cafe.name}
                    style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }}
                  />
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: '#1c1917', lineHeight: 1.3 }}>{cafe.name}</p>
                    <p style={{ margin: '3px 0 0', fontSize: 11, color: '#78716c' }}>{cafe.area}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                      <span style={{ fontSize: 11, color: cafe.isOpen ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                        {cafe.isOpen ? '● Buka' : '● Tutup'}
                      </span>
                      <span style={{ fontSize: 11, color: '#78716c' }}>·</span>
                      <span style={{ fontSize: 11, color: '#D97706', fontWeight: 700 }}>★ {cafe.rating}</span>
                    </div>
                    <p style={{ margin: '3px 0 0', fontSize: 11, color: '#78716c' }}>
                      Rp{(cafe.priceMin / 1000).toFixed(0)}K – Rp{(cafe.priceMax / 1000).toFixed(0)}K
                    </p>
                  </div>
                </div>
                <a
                  href={`/coffeshop-detail-page?id=${cafe.id}`}
                  style={{
                    display: 'block',
                    marginTop: 10,
                    padding: '7px 0',
                    background: '#D97706',
                    color: '#fff',
                    borderRadius: 8,
                    textAlign: 'center',
                    fontSize: 12,
                    fontWeight: 700,
                    textDecoration: 'none',
                  }}
                >
                  Lihat Detail →
                </a>
              </div>
            </InfoWindow>
          );
        })()}
      </GoogleMap>

      {/* Near Me Button */}
      <button
        onClick={handleNearMe}
        className="absolute bottom-4 right-4 flex items-center gap-2 bg-card shadow-card rounded-xl px-4 py-2.5 text-sm font-600 text-primary hover:shadow-card-hover transition-all btn-press z-10"
      >
        <Navigation size={15} className="text-accent" />
        Dekat kamu
      </button>

      {/* Cafe count badge */}
      <div className="absolute top-3 left-3 bg-card/90 backdrop-blur-sm rounded-xl px-3 py-1.5 shadow-card z-10">
        <span className="text-xs font-700 text-primary">{cafes.length} cafe di peta</span>
      </div>
    </div>
  );
}

// Fallback map when Google Maps API key is not set
function FallbackMap({ cafes, selectedId, onSelect }: { cafes: Cafe[]; selectedId: string | null; onSelect: (id: string | null) => void }) {
  const selectedCafe = cafes.find((c) => c.id === selectedId);

  return (
    <div className="relative w-full h-full rounded-2xl overflow-hidden border border-border bg-secondary">
      <div className="w-full h-full bg-gradient-to-br from-[#E8D5BE] to-[#F0E6D6] flex items-center justify-center relative overflow-hidden">
        {/* Grid lines */}
        <div className="absolute inset-0 opacity-20">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`hline-${i}`} className="absolute w-full border-t border-primary/30" style={{ top: `${(i + 1) * 12.5}%` }} />
          ))}
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={`vline-${i}`} className="absolute h-full border-l border-primary/30" style={{ left: `${(i + 1) * 12.5}%` }} />
          ))}
        </div>

        {/* Cafe Pins */}
        {cafes.slice(0, 15).map((cafe, i) => {
          const left = 10 + (i * 7.5) % 80;
          const top = 15 + (i * 11) % 70;
          const isSelected = cafe.id === selectedId;
          return (
            <button
              key={`pin-${cafe.id}`}
              onClick={() => onSelect(isSelected ? null : cafe.id)}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-200"
              style={{ left: `${left}%`, top: `${top}%` }}
            >
              <div className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full shadow-card transition-all ${
                isSelected ? 'bg-accent text-white scale-110 shadow-fab' : 'bg-card text-primary hover:bg-accent hover:text-white hover:scale-105'
              }`}>
                <span className="text-xs">☕</span>
                <span className="text-xs font-700 whitespace-nowrap max-w-[80px] overflow-hidden">{cafe.name.split(' ')[0]}</span>
              </div>
            </button>
          );
        })}

        <div className="text-center pointer-events-none">
          <p className="text-lg font-800 text-primary/30">Malang, Jawa Timur</p>
          <p className="text-sm text-muted-foreground/50 mt-1">Tambahkan NEXT_PUBLIC_GOOGLE_MAPS_API_KEY untuk peta interaktif</p>
        </div>

        <button className="absolute bottom-4 right-4 flex items-center gap-2 bg-card shadow-card rounded-xl px-4 py-2.5 text-sm font-600 text-primary hover:shadow-card-hover transition-all btn-press">
          <Navigation size={15} className="text-accent" />
          Dekat kamu
        </button>
      </div>

      {selectedCafe && (
        <div className="absolute bottom-0 left-0 right-0 bg-card border-t border-border rounded-t-2xl p-4 animate-slide-up shadow-card">
          <div className="flex items-start gap-3">
            <div className="relative w-16 h-16 rounded-xl overflow-hidden flex-shrink-0">
              <AppImage src={selectedCafe.photos[0]} alt={`Foto ${selectedCafe.name}`} fill className="object-cover" sizes="64px" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-800 text-foreground line-clamp-1">{selectedCafe.name}</h3>
                <button onClick={() => onSelect(null)} className="p-1 hover:bg-muted rounded-full transition-colors">
                  <X size={14} className="text-muted-foreground" />
                </button>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={selectedCafe.isOpen ? 'open-badge' : 'closed-badge'} style={{ fontSize: '10px' }}>
                  {selectedCafe.isOpen ? 'Buka' : 'Tutup'}
                </span>
                <span className="text-xs text-muted-foreground">{selectedCafe.area}</span>
                <div className="flex items-center gap-0.5 ml-auto">
                  <Star size={11} className="rating-star fill-current" />
                  <span className="text-xs font-700 font-tabular">{selectedCafe.rating}</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1 font-tabular">
                Rp{(selectedCafe.priceMin / 1000).toFixed(0)}K – Rp{(selectedCafe.priceMax / 1000).toFixed(0)}K
              </p>
            </div>
          </div>
          <Link href={`/coffeshop-detail-page?id=${selectedCafe.id}`} className="btn-primary w-full mt-3 text-sm">
            Lihat Detail
          </Link>
        </div>
      )}
    </div>
  );
}